import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';

import { CopyFeedParams } from 'src/app/api/services/feed-api.service';
import {
  FeedValidationFailed,
  FeedValidationRequestFailed,
  injectCreateFeedMutation,
  injectDuplicateFeedsMutation,
  injectEditFeedMutation,
  injectFeedWriteRefresh,
} from 'src/app/api/queries/feed.queries';

import * as feedActions from '../state/feed.actions';
import { submitFormFailure, submitFormSuccess } from '../state/form.actions';
import { deselectObjects } from '../state/selected.actons';
import { DuplicateObjectErrorItem } from '../models/core.models';
import { EditorFormEntity } from '../models/form.models';
import { WorkspaceObjectsService } from './workspace-objects.service';

/** Which of the two save chains a failure came from. */
type SaveKind = 'create' | 'edit';

/**
 * Creating, editing and duplicating a feed.
 *
 * The requests live in `feed.queries.ts`; what lives here is the **broadcast
 * set**, which is why this is a service and not a hook per call site. Five
 * components save feeds, and each has to raise the same eight-or-so actions in
 * the same order, including three distinguishable failure shapes.
 *
 * The feed object is reducer state, so most consumers are fed by these actions:
 * two reducers replace the feed, `core.reducer` clears `creatingFeed`,
 * `filtersChanged$` reloads the posts, `feedCreateSuccess$` toasts and
 * navigates, and five components close or reset themselves off `ofType`. The one
 * query cache this owner writes is the workspace-objects list, patched here
 * right after each response.
 */
@Injectable({ providedIn: 'root' })
export class FeedPersistenceService {
  private readonly store = inject(Store);
  private readonly workspaceObjects = inject(WorkspaceObjectsService);
  private readonly feedWriteRefresh = injectFeedWriteRefresh();
  private readonly createFeedMutation = injectCreateFeedMutation();
  private readonly editFeedMutation = injectEditFeedMutation();
  private readonly duplicateFeedsMutation = injectDuplicateFeedsMutation();

  /**
   * Validate the query, then create the feed.
   *
   * `createFeed` is dispatched first and is **not** just a marker: `core.reducer`
   * raises `creatingFeed` off it, which is the wizard's re-entrancy guard;
   * `createFeedSuccess` / `createFeedFail` lower it.
   */
  async create(feedData: any): Promise<void> {
    this.store.dispatch(feedActions.createFeed({ feedData }));

    try {
      const feed = await this.createFeedMutation.mutateAsync({ feedData });

      this.workspaceObjects.appendFeed(feed);
      this.store.dispatch(feedActions.createFeedSuccess({ feed }));
      this.store.dispatch(
        submitFormSuccess({
          entity: EditorFormEntity.Feed,
          object: feed,
          isCreated: true,
        }),
      );
    } catch (error) {
      this.reportSaveFailure('create', error);
    }
  }

  /** Validate the query (with the feed's own id in scope), then save it. */
  async edit(feedId: string, feedData: any): Promise<void> {
    try {
      const feed = await this.editFeedMutation.mutateAsync({
        feedId,
        feedData,
      });

      this.workspaceObjects.replaceFeed(feedId, feed);
      this.store.dispatch(feedActions.editFeedSuccess({ feedId, feed }));
      // * an edit changes what the feed's queries answer - reload posts,
      // * widgets and expanded filters
      this.feedWriteRefresh();
      this.store.dispatch(
        submitFormSuccess({
          entity: EditorFormEntity.Feed,
          object: feed,
          isCreated: false,
        }),
      );
    } catch (error) {
      this.reportSaveFailure('edit', error);
    }
  }

  /**
   * Copy one or several feeds.
   *
   * The endpoint answers **200 with an `error` body** when a business constraint
   * blocks the copy, so success and constraint-error are two branches of the same
   * resolved response. Only a real success deselects the source objects — a
   * constraint error keeps the selection so the user can retry — and the ids come
   * from the request, because the response carries the *new* objects.
   */
  async duplicate(copyFeedParams: CopyFeedParams): Promise<void> {
    try {
      const response =
        await this.duplicateFeedsMutation.mutateAsync(copyFeedParams);

      if ('error' in response) {
        const errors: DuplicateObjectErrorItem[] = Object.values(
          response.errors,
        ).sort((a, b) => a.display_order - b.display_order);

        this.store.dispatch(
          feedActions.duplicateFeedsConstraintError({ errors }),
        );
        return;
      }

      this.workspaceObjects.appendFeeds(response.object.feeds);
      this.store.dispatch(
        deselectObjects({ objectIds: copyFeedParams.object_ids }),
      );
    } catch (error) {
      this.store.dispatch(feedActions.duplicateFeedsFail({ error }));
    }
  }

  /**
   * Three failure shapes, kept distinct: the parser rejecting the query
   * (`charPos`), the validate request failing (`column`, off `error.response`),
   * and the save itself failing (the raw error).
   */
  private reportSaveFailure(kind: SaveKind, error: unknown): void {
    const fail =
      kind === 'create' ? feedActions.createFeedFail : feedActions.editFeedFail;

    if (error instanceof FeedValidationFailed) {
      this.store.dispatch(fail({ error: error.validation }));
      this.store.dispatch(
        submitFormFailure({
          entity: EditorFormEntity.Feed,
          error: {
            type: 'validation',
            message: error.validation.message,
            line: error.validation.line,
            charPos: error.validation.charPos,
          },
        }),
      );
      return;
    }

    if (error instanceof FeedValidationRequestFailed) {
      const httpError = error.httpError;

      this.store.dispatch(fail({ error: httpError }));
      this.store.dispatch(
        submitFormFailure({
          entity: EditorFormEntity.Feed,
          error: {
            type: 'validation',
            message: httpError?.response?.message,
            line: httpError?.response?.line,
            column: httpError?.response?.column,
          },
        }),
      );
      return;
    }

    this.store.dispatch(fail({ error }));
    this.store.dispatch(
      submitFormFailure({ entity: EditorFormEntity.Feed, error }),
    );
  }
}
